<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="房间" prop="roomId">
        <el-select
          v-model="queryParams.roomId"
          class="!w-240px"
          clearable
          filterable
          placeholder="请选择房间"
          @change="handleQuery"
        >
          <el-option v-for="item in roomList" :key="item.id" :label="item.roomNo || ('#' + item.id)" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="告警类型" prop="alertType">
        <el-input
          v-model="queryParams.alertType"
          class="!w-240px"
          clearable
          placeholder="请输入告警类型"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="处理状态" prop="handleStatus">
        <el-select
          :model-value="queryParams.handleStatus as any"
          @update:model-value="(val: any) => queryParams.handleStatus = val"
          class="!w-240px"
          clearable
          placeholder="请选择处理状态"
        >
          <el-option label="未处理" :value="0" />
          <el-option label="已处理" :value="1" />
          <el-option label="已忽略" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column align="center" label="房间号" prop="roomNo" min-width="120">
        <template #default="scope">
          <el-tag v-if="scope.row.roomNo" type="info">{{ scope.row.roomNo }}</el-tag>
          <span v-else class="text-gray-400">#{{ scope.row.roomId }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="告警类型" prop="alertType">
        <template #default="scope">
          <el-tag>{{ getAlertTypeLabel(scope.row.alertType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="告警级别" prop="alertLevel">
        <template #default="scope">
          <el-tag v-if="scope.row.alertLevel === 0" type="success">低</el-tag>
          <el-tag v-else-if="scope.row.alertLevel === 1" type="warning">中</el-tag>
          <el-tag v-else-if="scope.row.alertLevel === 2" type="danger">高</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="告警内容" prop="alertContent" min-width="200" />
      <el-table-column align="center" label="处理状态" prop="handleStatus">
        <template #default="scope">
          <el-tag v-if="scope.row.handleStatus === 0" type="danger">未处理</el-tag>
          <el-tag v-else-if="scope.row.handleStatus === 1" type="success">已处理</el-tag>
          <el-tag v-else-if="scope.row.handleStatus === 2" type="info">已忽略</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="处理人" prop="handleUser" />
      <el-table-column align="center" label="处理备注" prop="handleRemark" min-width="150" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
      />
      <el-table-column align="center" label="操作" width="150">
        <template #default="scope">
          <el-button
            v-if="scope.row.handleStatus === 0"
            link
            type="primary"
            @click="openHandleDialog(scope.row.id)"
            v-hasPermi="['hotel:room-alert:update']"
          >
            处理
          </el-button>
          <el-button
            v-if="scope.row.handleStatus === 0"
            link
            type="warning"
            @click="handleIgnore(scope.row.id)"
            v-hasPermi="['hotel:room-alert:update']"
          >
            忽略
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="handleDialogVisible" title="处理告警">
    <el-form
      ref="handleFormRef"
      :model="handleFormData"
      :rules="handleFormRules"
      label-width="100px"
    >
      <el-form-item label="处理备注" prop="handleRemark">
        <el-input
          v-model="handleFormData.handleRemark"
          :rows="3"
          placeholder="请输入处理备注"
          type="textarea"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button type="primary" @click="submitHandle">确 定</el-button>
      <el-button @click="handleDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import * as roomAlertApi from '@/api/hotel/roomAlert'
import * as roomApi from '@/api/hotel/room'

defineOptions({ name: 'HotelRoomAlert' })

const message = useMessage()

const loading = ref(true)
const total = ref(0)
const list = ref([])

// 房间下拉列表
const roomList = ref<any[]>([])
const getRoomList = async () => {
  const data = await roomApi.listAllRooms()
  roomList.value = data.list || []
}

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  roomId: undefined,
  alertType: undefined,
  handleStatus: undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    // 房间号由后端分页接口批量补全（HotelRoomAlertController#getAlertPage），
    // 禁止再按行调 /hotel/room/get，否则 20 行会产生 20 次请求（T2-02 N+1）
    const data = await roomAlertApi.getRoomAlertPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

// 告警类型映射
const alertTypeMap: Record<string, string> = {
  'OVERSOLD': '超售',
  'IDLE': '长期空置',
  'LONG_VACANT': '长期空置',
  'CLEANING_TIMEOUT': '清洁超时',
  'MAINTENANCE_TIMEOUT': '维修超时',
  'PRICE_ABNORMAL': '价格异常',
  'INVENTORY_ABNORMAL': '库存异常',
  'DEVICE_FAULT': '设备故障',
  'GUEST_COMPLAINT': '客人投诉'
}
const getAlertTypeLabel = (t: string) => alertTypeMap[t] || t || '-'

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value.resetFields()
  handleQuery()
}

const handleDialogVisible = ref(false)
const handleFormData = reactive({
  handleRemark: ''
})
const handleFormRules = reactive({
  handleRemark: [{ required: true, message: '处理备注不能为空', trigger: 'blur' }]
})
const handleFormRef = ref()
const currentAlertId = ref<string | number>(0)

const openHandleDialog = (id: string | number) => {
  currentAlertId.value = id
  handleFormData.handleRemark = ''
  handleDialogVisible.value = true
}

const submitHandle = async () => {
  if (!handleFormRef) return
  const valid = await handleFormRef.value.validate()
  if (!valid) return
  try {
    await roomAlertApi.handleAlert(currentAlertId.value, {
      handleRemark: handleFormData.handleRemark
    })
    message.success('处理成功')
    handleDialogVisible.value = false
    await getList()
  } catch {}
}

const handleIgnore = async (id: string | number) => {
  try {
    await message.confirm('确认忽略该告警？')
    await roomAlertApi.ignoreAlert(id)
    message.success('已忽略')
    await getList()
  } catch {}
}

onMounted(() => {
  getList()
  getRoomList()
})
</script>
